/**
 * The admin screen's wire surface: clubs, plans, members and grants.
 *
 * Follows `api/me.ts`'s rules rather than `api/journal.ts`'s. Reads degrade —
 * a missing server or a failed fetch is an empty list, and the screen says it
 * has nothing rather than erroring. **Writes throw**, because an admin who
 * ticked a capability and saw nothing happen would assume it saved, and the
 * whole point of this screen is that somebody trusts what it shows.
 *
 * Every path is `/admin/*` on qv-data, guarded server-side by the `admin` role
 * in `user_role` — never by a capability. Gating the editor on its own subject
 * matter means one bad grant locks every admin out of the screen that would
 * undo it.
 */

import { requestHeaders } from "../lib/user";
import { httpBase } from "./backend";

const BASE = httpBase(import.meta.env.VITE_QV_SET_URL);

/** Whether there is a server to ask at all. */
export const adminApiConfigured: boolean = BASE !== "";

async function get<T>(path: string, fallback: T): Promise<T> {
  if (!BASE) return fallback;
  try {
    const response = await fetch(`${BASE}${path}`, { headers: await requestHeaders() });
    if (!response.ok) return fallback;
    return (await response.json()) as T;
  } catch {
    return fallback;
  }
}

async function write<T>(path: string, method: string, body?: unknown): Promise<T> {
  if (!BASE) throw new Error("no server configured");
  const headers = await requestHeaders();
  const response = await fetch(`${BASE}${path}`, {
    method,
    headers: body === undefined ? headers : { ...headers, "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) {
    // The server's own sentence, which names the capability or the conflict.
    // A generic "failed" here would send an admin to the logs for something
    // the response already explained.
    throw new Error((await response.text()) || `${method} ${path} failed`);
  }
  return (await response.json()) as T;
}

// ── the vocabulary ──────────────────────────────────────────────────────────

/** One grantable value inside a domain — a market, an indicator group, a universe. */
export interface CapabilityValue {
  value: string;
  label: string;
  market?: string | null;
}

export interface CapabilityDomain {
  domain: string;
  label: string;
  values: CapabilityValue[];
}

export interface FeatureCapability {
  capability: string;
  label: string;
  description: string;
}

export interface LimitCapability {
  capability: string;
  label: string;
  /** The `feat:` this budget belongs to. The feature decides *whether*. */
  gatedBy: string;
}

export interface Vocabulary {
  domains: CapabilityDomain[];
  features: FeatureCapability[];
  limits: LimitCapability[];
}

const EMPTY_VOCABULARY: Vocabulary = { domains: [], features: [], limits: [] };

/**
 * Every capability that can be granted, with real values behind each domain.
 *
 * Served rather than hard-coded because three domains are **data**: indicator
 * categories come from the chart catalog, groups from the universe, markets
 * from the registry. A typed-in capability box would let an admin grant
 * `ind:Seasonalty` and see no error until a user complained.
 */
export function fetchVocabulary(): Promise<Vocabulary> {
  return get("/admin/vocabulary", EMPTY_VOCABULARY);
}

// ── grants ──────────────────────────────────────────────────────────────────

export type SubjectKind = "plan" | "org" | "user";

export interface Grant {
  capability: string;
  limit: number | null;
  effect: "allow" | "deny";
  grantedAt: string;
}

export interface PlanGrants {
  plan: string;
  grants: Grant[];
}

export function fetchPlans(): Promise<PlanGrants[]> {
  return get("/admin/plans", [] as PlanGrants[]);
}

export function fetchGrants(kind: SubjectKind, key: string): Promise<Grant[]> {
  return get(`/admin/grants/${kind}/${encodeURIComponent(key)}`, [] as Grant[]);
}

export function setGrant(
  kind: SubjectKind,
  key: string,
  capability: string,
  options: { limit?: number | null; effect?: "allow" | "deny" } = {},
): Promise<Grant> {
  return write(`/admin/grants/${kind}/${encodeURIComponent(key)}`, "PUT", {
    capability,
    limit: options.limit ?? null,
    effect: options.effect ?? "allow",
  });
}

export function revokeGrant(
  kind: SubjectKind,
  key: string,
  capability: string,
): Promise<{ removed: number }> {
  // The capability is **not** encoded: the route takes it as `:path` precisely
  // because it contains colons, and `screen:preset:x` is one value rather than
  // three segments. Encoding the colons would make the server look for a
  // capability spelled with `%3A`.
  return write(`/admin/grants/${kind}/${encodeURIComponent(key)}/${capability}`, "DELETE");
}

// ── clubs ───────────────────────────────────────────────────────────────────

export interface Org {
  key: string;
  name: string;
  description: string | null;
  /** Legacy — `app_user.tier`. Nothing new should set it. */
  plan: string;
  disabled: boolean;
  createdAt: string;
  memberCount?: number;
  /**
   * A virtual group's includes — `["grp-jas", "grp-jp"]` — or null for an
   * ordinary group (`docs/plans/group-sharing.md`). Its members receive what
   * every included group publishes.
   */
  includes?: string[] | null;
}

export interface Member {
  userKey: string;
  role: "member" | "admin";
  joinedAt: string;
}

export function fetchOrgs(): Promise<Org[]> {
  return get("/admin/orgs", [] as Org[]);
}

export function createOrg(input: {
  key: string;
  name: string;
  plan?: string;
  description?: string;
}): Promise<Org> {
  return write("/admin/orgs", "POST", input);
}

export function patchOrg(key: string, changes: Partial<Org>): Promise<Org> {
  return write(`/admin/orgs/${encodeURIComponent(key)}`, "PATCH", changes);
}

/**
 * Make a group virtual — its members receive from every group it includes — or,
 * with `null` or `[]`, ordinary again. **The one place that knows the route**,
 * so moving it is a one-line change. The server refuses a virtual group among
 * the includes (one level deep, rule 5), and that sentence is what throws.
 */
export function setOrgIncludes(key: string, includes: string[] | null): Promise<Org> {
  return write(`/admin/orgs/${encodeURIComponent(key)}`, "PATCH", {
    includes: includes && includes.length > 0 ? includes : null,
  });
}

export function fetchMembers(key: string): Promise<Member[]> {
  return get(`/admin/orgs/${encodeURIComponent(key)}/members`, [] as Member[]);
}

export function addMember(
  key: string,
  userKey: string,
  role: "member" | "admin" = "member",
): Promise<Member> {
  return write(`/admin/orgs/${encodeURIComponent(key)}/members`, "POST", { userKey, role });
}

export function removeMember(key: string, userKey: string): Promise<{ removed: number }> {
  return write(
    `/admin/orgs/${encodeURIComponent(key)}/members/${encodeURIComponent(userKey)}`,
    "DELETE",
  );
}

// ── accounts ────────────────────────────────────────────────────────────────

export interface AdminUser {
  key: string;
  email: string | null;
  displayName: string | null;
  plan: string;
  isTest: boolean;
  disabled: boolean;
  orgs: string[];
}

/**
 * Accounts, searched by key **or email** — the server matches either.
 *
 * `limit` is the server's own parameter, capped at 200 there. It is passed
 * where the caller wants a *directory* rather than a search result: a club
 * membership stores a key, and a key is only readable once there is an account
 * beside it to name.
 */
export function fetchUsers(query = "", limit = 0): Promise<AdminUser[]> {
  const params = new URLSearchParams();
  if (query.trim()) params.set("q", query.trim());
  if (limit > 0) params.set("limit", String(limit));
  const suffix = params.toString();
  return get(`/admin/users${suffix ? `?${suffix}` : ""}`, [] as AdminUser[]);
}

export function setUserPlan(key: string, plan: string): Promise<{ key: string; plan: string }> {
  return write(`/admin/users/${encodeURIComponent(key)}`, "PATCH", { plan });
}

/** One source that contributed to a resolved answer. */
export interface EffectiveSource {
  kind: SubjectKind;
  key: string;
  /** Set when this plan is reached *through* a club rather than the user's own. */
  via?: string;
  grants: Grant[];
}

export interface Effective {
  key: string;
  plan: string;
  orgs: string[];
  permissive: boolean;
  capabilities: string[];
  limits: Record<string, number>;
  sources: EffectiveSource[];
}

const EMPTY_EFFECTIVE: Effective = {
  key: "",
  plan: "",
  orgs: [],
  permissive: true,
  capabilities: [],
  limits: {},
  sources: [],
};

/**
 * What one account actually holds, and where each part came from.
 *
 * The merge is a union across a plan, any number of clubs and the user's own
 * row, so "why can they not see this" has several possible answers and no
 * single row holding it. A screen showing only the result answers "can they",
 * never "why" — and "why" is the question an admin arrives with.
 */
export function fetchEffective(key: string): Promise<Effective> {
  return get(`/admin/users/${encodeURIComponent(key)}/effective`, EMPTY_EFFECTIVE);
}

// ── screener presets ────────────────────────────────────────────────────────

export interface AdminPreset {
  key: string;
  name: string;
  description: string | null;
  ownerKind: "system" | "org";
  ownerKey: string | null;
  market: string;
  groupCodes: string[];
  isActive: boolean;
  updatedAt: string | null;
}

/** Every preset, **parked ones included** — this is where one is un-parked. */
export function fetchAdminPresets(): Promise<AdminPreset[]> {
  return get("/admin/presets", [] as AdminPreset[]);
}

/**
 * Park or un-park a preset.
 *
 * A `PUT` replaces the whole definition, so the current one is read back and
 * resent with `isActive` flipped. Sending only the flag would blank the columns
 * and the filter — the store replaces rather than merges, deliberately, because
 * merging a partial definition produces a screen nobody authored.
 */
export async function setPresetActive(key: string, isActive: boolean): Promise<void> {
  const whole = await get<AdminPreset & { columns?: unknown; baseFilter?: unknown; sorts?: unknown }>(
    `/admin/presets/${encodeURIComponent(key)}`,
    null as never,
  );
  if (!whole) throw new Error(`no preset ${key}`);
  await write(`/admin/presets/${encodeURIComponent(key)}`, "PUT", { ...whole, isActive });
}

/** Remove one published by mistake. Parking is the reversible option. */
export function deletePreset(key: string): Promise<{ removed: number }> {
  return write(`/admin/presets/${encodeURIComponent(key)}`, "DELETE");
}

// ── published layouts ────────────────────────────────────────────────────────
//
// The preset block above, for arrangements. A copy in shape rather than a
// generic "publishable" helper over both: the two share the words key, owner,
// park and delete and share nothing else, so the abstraction would take a
// parameter list of everything either one needs.

export interface AdminLayout {
  key: string;
  name: string;
  description: string | null;
  ownerKind: "system" | "org";
  ownerKey: string | null;
  chartCount: number;
  isActive: boolean;
  updatedAt: string | null;
}

/** Every published layout, **parked ones included** — this is where one is un-parked. */
export function fetchAdminLayouts(): Promise<AdminLayout[]> {
  return get("/admin/layouts", [] as AdminLayout[]);
}

/**
 * Park or un-park a layout.
 *
 * The whole thing is read back and resent with `isActive` flipped, for the
 * preset's reason: a `PUT` replaces rather than merges, so sending the flag
 * alone would blank the arrangement — and a layout with no sections is refused
 * by the store, so this would fail loudly rather than quietly. Loud is better
 * and still not what anybody wanted.
 */
export async function setLayoutActive(key: string, isActive: boolean): Promise<void> {
  const whole = await get<AdminLayout & { definition?: unknown }>(
    `/admin/layouts/${encodeURIComponent(key)}`,
    null as never,
  );
  if (!whole) throw new Error(`no layout ${key}`);
  await write(`/admin/layouts/${encodeURIComponent(key)}`, "PUT", { ...whole, isActive });
}

/** Remove one published by mistake. Parking is the reversible option. */
export function deleteLayout(key: string): Promise<{ removed: number }> {
  return write(`/admin/layouts/${encodeURIComponent(key)}`, "DELETE");
}

// ── published chart templates ────────────────────────────────────────────────
//
// The two blocks above, a third time. A generic "publishable" helper over all
// three would take a parameter list of everything any of them needs — they
// share the words key, owner, park and delete and share nothing else.

export interface AdminChartTemplate {
  key: string;
  name: string;
  description: string | null;
  category: string | null;
  ownerKind: "system" | "org" | "user";
  ownerKey: string | null;
  paneCount: number;
  symbol: string | null;
  isActive: boolean;
  updatedAt: string | null;
}

/** Every chart template, **parked ones included** — this is where one is un-parked. */
export function fetchAdminChartTemplates(): Promise<AdminChartTemplate[]> {
  return get("/admin/chart-templates", [] as AdminChartTemplate[]);
}

/**
 * Park or un-park a chart template.
 *
 * The whole thing is read back and resent with `isActive` flipped, for the two
 * above: a `PUT` replaces rather than merges, so sending the flag alone would
 * blank the definition — and a template with no panes is refused by the store,
 * so it would fail loudly rather than quietly. Loud is better and still not
 * what anybody wanted.
 *
 * **`keepSymbol` is resent as whether there is one**, not as `true`. The stored
 * definition has already had the choice applied, so re-sending `true` over a
 * template deliberately saved without a symbol would be harmless today and
 * wrong the moment the definition carries one for another reason.
 */
export async function setChartTemplateActive(key: string, isActive: boolean): Promise<void> {
  const whole = await get<AdminChartTemplate & { definition?: { symbol?: string } }>(
    `/admin/chart-templates/${encodeURIComponent(key)}`,
    null as never,
  );
  if (!whole) throw new Error(`no chart template ${key}`);
  await write(`/admin/chart-templates/${encodeURIComponent(key)}`, "PUT", {
    ...whole,
    isActive,
    keepSymbol: Boolean(whole.definition?.symbol),
  });
}

/** Remove one published by mistake. Parking is the reversible option. */
export function deleteChartTemplate(key: string): Promise<{ removed: number }> {
  return write(`/admin/chart-templates/${encodeURIComponent(key)}`, "DELETE");
}

// ── sales: what a group (or tier) sells ──────────────────────────────────────

/** One price on sale. `amount` in minor units (cents), as Stripe holds it. */
export interface SalePrice {
  interval: string;
  priceId: string;
  amount: number;
  currency: string;
  createdAt: string;
}

/**
 * `/admin/billing/plans/{key}` — the admin's own brand only.
 *
 * `sold` is derived on the server, never stored: `q` while any price is on
 * sale, else `none`. `O` (the group sells on Connect) is not built yet.
 */
export interface GroupSales {
  brand: string;
  plan: string;
  /** One of the brand's tiers (EOD, Live) rather than a Q group. */
  tier: boolean;
  sold: "q" | "none";
  name: string;
  description: string;
  cta: string;
  prices: SalePrice[];
  buyers: { monthly: number; term: number; forever: number };
}

const salesPath = (key: string) => `/admin/billing/plans/${encodeURIComponent(key)}`;

/** Null when billing is not configured for this brand, or the read failed. */
export function getGroupSales(key: string): Promise<GroupSales | null> {
  return get<GroupSales | null>(salesPath(key), null);
}

/** Put a price on sale; `amount` in major units, as typed (`199.00`). */
export function addGroupPrice(key: string, interval: string, amount: string): Promise<GroupSales> {
  return write<GroupSales>(`${salesPath(key)}/prices`, "POST", { interval, amount });
}

/** Stop selling one price. Whoever already bought it keeps it. */
export function stopGroupPrice(key: string, priceId: string): Promise<GroupSales> {
  return write<GroupSales>(
    `${salesPath(key)}/prices/${encodeURIComponent(priceId)}/stop`,
    "POST",
  );
}

/** The card's one line. */
export function setGroupWords(key: string, description: string): Promise<GroupSales> {
  return write<GroupSales>(`${salesPath(key)}/words`, "PUT", { description });
}
